import * as React from 'react';

import { enumInvoiceStatus } from '@rematter/frontend-lib-api-client';
import { Button, EntityDetails, Loading, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';

import {
  $invoiceError,
  $invoiceId,
  $isInvoiceLoading,
  $markInvoiceAsPaid,
  $remoteInvoiceDetail,
  $tab,
  $voidInvoice,
} from '../../_state/invoiceDetail.state';
import { financeRoutes } from '../../routes';
import { InvoiceDetailsTab, InvoiceRecord } from '../../types';
import { FinanceRootLayout } from '../_layouts/FinanceRootLayout';
import { InvoiceStatusBadge } from '../_shared/InvoiceStatusBadge';

import { InvoiceTabInfo } from './InvoiceTabInfo';
import { InvoiceTabLineItems } from './InvoiceTabLineItems';
import { InvoiceTabPayments } from './InvoiceTabPayments';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ INVOICE DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const InvoiceDetailView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Atoms
  const invoiceId = useAtomValue($invoiceId);
  const queryResult = useAtomValue($remoteInvoiceDetail);
  const invoice = (
    queryResult?.data as { invoice: InvoiceRecord | null } | undefined
  )?.invoice;
  const refetch = queryResult?.refetch;
  const isLoading = useAtomValue($isInvoiceLoading);
  const error = useAtomValue($invoiceError);
  const [tab, setTab] = useAtom($tab);
  const voidInvoice = useSetAtom($voidInvoice);
  const markInvoiceAsPaid = useSetAtom($markInvoiceAsPaid);

  // Other hooks
  const toast = useToast();

  const activeTab = (tab || 'info') as InvoiceDetailsTab;

  // Memoized values
  const isDisabled = React.useMemo(
    () =>
      invoice?.status === enumInvoiceStatus.Paid ||
      invoice?.status === enumInvoiceStatus.Voided,
    [invoice?.status],
  );

  const infoTabContent = React.useMemo(
    () => () => <InvoiceTabInfo disabled={isDisabled} />,
    [isDisabled],
  );

  const lineItemsTabContent = React.useMemo(
    () => () => <InvoiceTabLineItems disabled={isDisabled} />,
    [isDisabled],
  );

  const paymentsTabContent = React.useMemo(
    () => () => <InvoiceTabPayments disabled={isDisabled} />,
    [isDisabled],
  );

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(financeRoutes.invoices);
  }, [navigate]);

  const handleVoid = React.useCallback(async () => {
    if (!invoiceId) return;
    try {
      await voidInvoice({ id: invoiceId });
      toast.success(t.messages.invoices.voidSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.messages.invoices.voidError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    }
  }, [invoiceId, voidInvoice, toast, refetch, t]);

  const handleMarkAsPaid = React.useCallback(async () => {
    if (!invoiceId) return;
    try {
      await markInvoiceAsPaid({ id: invoiceId });
      toast.success(t.messages.invoices.markAsPaidSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.messages.invoices.markAsPaidError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    }
  }, [invoiceId, markInvoiceAsPaid, toast, refetch, t]);

  const menuItems = React.useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      onAction: () => void;
      disabled?: boolean;
    }> = [];

    if (invoice?.status === enumInvoiceStatus.Pending) {
      items.push({
        id: 'markAsPaid',
        label: t.invoices.detail.actions.markAsPaid,
        onAction: handleMarkAsPaid,
      });
    }

    if (!isDisabled) {
      items.push({
        id: 'void',
        label: t.invoices.detail.actions.void,
        onAction: handleVoid,
      });
    }

    return items;
  }, [invoice?.status, isDisabled, handleVoid, handleMarkAsPaid, t]);

  if (error) {
    return (
      <FinanceRootLayout>
        <div className="p-4 text-failure">
          {t.messages.invoices.loadError(error.message)}
        </div>
      </FinanceRootLayout>
    );
  }

  if (isLoading || !invoice) {
    return (
      <FinanceRootLayout>
        <div className="p-4">
          <Loading />
        </div>
      </FinanceRootLayout>
    );
  }

  return (
    <FinanceRootLayout className="@InvoiceDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as InvoiceDetailsTab)}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={`Invoice #${invoice.documentNumber}`}
        subtitle={invoice.accountName}
        badges={<InvoiceStatusBadge status={invoice.status} />}
        headerActions={
          menuItems.length > 0 && (
            <Menu
              trigger={
                <Button
                  icon={<LucideMoreHorizontal className="size-4" />}
                  variant="outline"
                  className="w-8"
                />
              }
              items={menuItems}
            />
          )
        }
        tabs={{
          info: {
            title: t.invoices.detail.tabs.info,
            content: infoTabContent,
          },
          'line-items': {
            title: t.invoices.detail.tabs.lineItems,
            content: lineItemsTabContent,
          },
          payments: {
            title: t.invoices.detail.tabs.payments,
            content: paymentsTabContent,
          },
        }}
      />
    </FinanceRootLayout>
  );
};
